import { createContext, useContext, useState, useCallback, useMemo } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const loginUser = useCallback((loginResponse) => {
    localStorage.setItem('token', loginResponse.token);
    const userData = {
      userId: loginResponse.userId,
      email: loginResponse.email,
      firstName: loginResponse.firstName,
      lastName: loginResponse.lastName,
      orgAssignments: loginResponse.orgAssignments || [],
      isSystemAdmin: loginResponse.systemAdmin || false,
    };
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const loginWithToken = useCallback((token) => {
    localStorage.setItem('token', token);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const isAdminForOrg = useCallback((orgId) => {
    if (!user?.orgAssignments) return false;
    return user.orgAssignments.some(
      (a) => a.organisationId === orgId && a.profileName === 'Admin'
    );
  }, [user]);

  const addOrgAssignment = useCallback((assignment) => {
    setUser((prev) => {
      const updated = {
        ...prev,
        orgAssignments: [...(prev.orgAssignments || []), assignment],
      };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const isAnyAdmin = useMemo(() => {
    if (!user?.orgAssignments) return false;
    return user.orgAssignments.some((a) => a.profileName === 'Admin');
  }, [user]);

  const isSystemAdmin = useMemo(() => {
    return !!user?.isSystemAdmin;
  }, [user]);

  /**
   * Check if the user has a specific feature permission at a given access level
   * for any of their organisations. Access levels are hierarchical:
   * DELETE > WRITE > READ.
   */
  const hasFeature = useCallback((featureCode, requiredLevel = 'READ') => {
    if (!user?.orgAssignments) return false;
    const levelOrder = { READ: 1, WRITE: 2, DELETE: 3 };
    const required = levelOrder[requiredLevel] || 1;

    return user.orgAssignments.some((a) => {
      if (!a.permissions) return false;
      const perm = a.permissions.find((p) => p.featureCode === featureCode);
      if (!perm) return false;
      return (levelOrder[perm.accessLevel] || 0) >= required;
    });
  }, [user]);

  /**
   * Check if the user has a specific feature permission for a specific org.
   */
  const hasFeatureForOrg = useCallback((orgId, featureCode, requiredLevel = 'READ') => {
    if (!user?.orgAssignments) return false;
    const levelOrder = { READ: 1, WRITE: 2, DELETE: 3 };
    const required = levelOrder[requiredLevel] || 1;

    const assignment = user.orgAssignments.find((a) => a.organisationId === orgId);
    if (!assignment?.permissions) return false;
    const perm = assignment.permissions.find((p) => p.featureCode === featureCode);
    if (!perm) return false;
    return (levelOrder[perm.accessLevel] || 0) >= required;
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user, loginUser, loginWithToken, logout, isAuthenticated: !!user,
      isAdminForOrg, isAnyAdmin, isSystemAdmin, addOrgAssignment,
      hasFeature, hasFeatureForOrg,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
